import type { ComponentProps, ReactElement, ReactNode } from 'react';
import StatusErrorIcon from '@atlaskit/icon/core/status-error';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import Select, {
  components,
  type ActionMeta,
  type OptionProps,
  type StylesConfig,
} from '@atlaskit/select';
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { FIELD_MAX_WIDTH } from '../fieldMaxWidth';

/**
 * Accent colors for a selected chip. Any object with these three resolved CSS
 * values works — e.g. the site-tile palette's `SiteTileColor` is assignable.
 */
export type ChipColor = {
  background: string;
  border: string;
  foreground: string;
};

const wrapperStyles = xcss({ width: '100%', alignSelf: 'stretch' });
const stackStyles = xcss({ width: '100%' });
const selectStyles = xcss({ width: '100%', maxWidth: FIELD_MAX_WIDTH });
const messageStyles = xcss({ maxWidth: FIELD_MAX_WIDTH });

/**
 * Internal react-select option. `value` is a stable string key derived from the
 * caller's `toValue`; the original `item` and its stored value ride along so we
 * can hand them back untouched in `onChange`.
 */
type InternalOption<TItem, TValue> = {
  value: string;
  label: string;
  description?: string | null;
  icon?: ReactNode;
  chipColor?: ChipColor;
  /** Overrides `label` on the selected chip only. Falls back to `label`. */
  chipLabel?: string;
  /** Which selection control leads the option row: checkbox for multi, radio for single. */
  controlType: 'checkbox' | 'radio';
  item: TItem;
  storedValue: TValue;
};

/** Live selection counts passed to `renderLabel`. */
export type SelectionCounts = { count: number; total: number };

export interface MultiSelectProps<TItem, TValue = string> {
  /** All selectable items. */
  items: readonly TItem[];
  /** Currently selected values (as produced by `toValue`). Order is preserved. */
  value: readonly TValue[];
  /** How to store a given item — the value pushed into the selection array. */
  toValue: (item: TItem) => TValue;
  /** Called with the new selection: the stored values and their source items. */
  onChange: (values: TValue[], items: TItem[]) => void;
  /** Primary line for each option (and the chip / value label). */
  getLabel: (item: TItem) => string;
  /** Optional subtle description rendered below the label. */
  getDescription?: (item: TItem) => string | null | undefined;
  /** Optional leading icon/avatar node for each option. */
  getIcon?: (item: TItem) => ReactNode;
  /**
   * Optional accent color for the selected chip. When provided, the chip gets a
   * colored background/border/foreground — mirroring the Rovo and Trello
   * workspace selectors. Omit for a default (neutral) chip.
   */
  getChipColor?: (item: TItem) => ChipColor;
  /**
   * Optional label for the selected chip, when it should read differently from
   * the option row — e.g. a site row shows its name over its URL, while the chip
   * has one line and shows both. Defaults to `getLabel`.
   */
  getChipLabel?: (item: TItem) => string;
  /**
   * Allow selecting more than one item. Defaults to false (single-select).
   *
   * Both modes render the selection as removable chips — react-select is always
   * in multi mode for that reason — so this only controls how many can be held
   * at once, plus whether an option leads with a checkbox or a radio.
   */
  isMulti?: boolean;
  /**
   * Maximum number of selections (multi-select only). Once reached, unselected
   * options are disabled; already-selected options stay removable. Omit for no
   * cap.
   */
  maxSelected?: number;
  /**
   * Disable a specific item in the menu while leaving it listed. Combined with
   * `maxSelected` when both apply. Omit to leave every option enabled (unless the
   * multi-select cap is on).
   */
  isItemDisabled?: (item: TItem) => boolean;
  inputId: string;
  placeholder?: string;
  /** Renders the field label above the select; receives the live counts. */
  renderLabel?: (counts: SelectionCounts) => ReactNode;
  isSearchable?: boolean;
  isClearable?: boolean;
  /** Disables the field (non-interactive). Defaults to false. */
  isDisabled?: boolean;
  /**
   * Optional message rendered below the field. Static markup, so it reads at
   * a glance rather than hiding behind the click and popup that
   * @atlaskit/inline-message brings — and any link inside it stays clickable.
   *
   * `error` (the default) leads with the ADS status-error icon. `information`
   * is helper copy with the status-information icon — used when options are
   * disabled, not when the user has done something wrong.
   */
  message?: ReactNode;
  messageTone?: 'error' | 'information';
}

/** Stable string key for a stored value, used for option identity / matching. */
const keyOf = (value: unknown): string => String(value);

/** Coerce react-select's onChange payload into a plain array. */
const toArray = <T,>(value: unknown): T[] =>
  Array.isArray(value) ? (value as T[]) : value ? [value as T] : [];

/**
 * Option row: leading selection control (checkbox in multi-select, radio in
 * single-select) + icon + label with an optional subtle description. Modeled on
 * the Rovo and Trello workspace selectors.
 *
 * A disabled option keeps its place in the list: the control renders disabled
 * and both lines of copy switch to `color.text.disabled`.
 */
export const OptionRow = ({
  data,
  controlType,
  isSelected,
  isDisabled,
}: {
  data: Pick<
    InternalOption<unknown, unknown>,
    'label' | 'description' | 'icon'
  >;
  controlType: 'checkbox' | 'radio';
  isSelected: boolean;
  isDisabled: boolean;
}): ReactElement => (
  <Inline space="space.100" alignBlock="center">
    <input
      type={controlType}
      checked={isSelected}
      disabled={isDisabled}
      readOnly
      tabIndex={-1}
      aria-hidden
    />
    {data.icon}
    <Stack>
      <Text
        weight="medium"
        color={isDisabled ? 'color.text.disabled' : undefined}
      >
        {data.label}
      </Text>
      {data.description && (
        <Text
          size="small"
          color={isDisabled ? 'color.text.disabled' : 'color.text.subtlest'}
        >
          {data.description}
        </Text>
      )}
    </Stack>
  </Inline>
);

/** Stable react-select adapter; presentation mode rides on each option. */
const SelectOption = (
  props: OptionProps<InternalOption<unknown, unknown>, boolean>,
): ReactElement => (
  <components.Option {...props}>
    <OptionRow
      data={props.data}
      controlType={props.data.controlType}
      isSelected={props.isSelected}
      isDisabled={props.isDisabled}
    />
  </components.Option>
);

/**
 * Chip text for the current selection. Prefers the option's `chipLabel` so a
 * chip can read differently from its (multi-line) option row.
 */
const ChipLabel = (
  props: ComponentProps<typeof components.MultiValueLabel>,
): ReactElement => {
  const data = props.data as Partial<InternalOption<unknown, unknown>>;
  return (
    <components.MultiValueLabel {...props}>
      {data.chipLabel ?? data.label}
    </components.MultiValueLabel>
  );
};

/**
 * Chip styling for the current selection. Reads the per-option `chipColor`, so
 * options without one keep react-select's default chip. Module scope keeps a
 * stable identity without memoization.
 *
 * Only the multiValue rules are needed: react-select always runs in multi mode
 * here (see below), so both modes render the selection as a removable chip.
 */
const chipStyles: StylesConfig<InternalOption<unknown, unknown>, boolean> = {
  multiValue: (base, state) => {
    const color = state.data.chipColor;
    return color
      ? {
          ...base,
          backgroundColor: color.background,
          borderColor: color.border,
          borderStyle: 'solid',
          borderWidth: token('border.width', '1px'),
        }
      : base;
  },
  multiValueLabel: (base, state) =>
    state.data.chipColor
      ? { ...base, color: state.data.chipColor.foreground }
      : base,
  multiValueRemove: (base, state) =>
    state.data.chipColor
      ? { ...base, color: state.data.chipColor.foreground }
      : base,
};

/**
 * Presentational single/multi select for a list of items, rendering each
 * selection as a removable chip.
 *
 * Selection is always represented as an array (0–1 entries in single-select), so
 * callers store it uniformly and can hand it straight to a wire format that
 * takes repeated params. The component is intentionally decoupled from consent
 * context — pass data in via props and persist it in `onChange`.
 */
function MultiSelect<TItem, TValue = string>({
  items,
  value,
  toValue,
  onChange,
  getLabel,
  getDescription,
  getIcon,
  getChipColor,
  getChipLabel,
  isMulti = false,
  maxSelected,
  isItemDisabled,
  inputId,
  placeholder,
  renderLabel,
  isSearchable = true,
  isClearable = true,
  isDisabled = false,
  message,
  messageTone = 'error',
}: MultiSelectProps<TItem, TValue>): ReactElement {
  const options: InternalOption<TItem, TValue>[] = items.map((item) => {
    const storedValue = toValue(item);
    return {
      value: keyOf(storedValue),
      label: getLabel(item),
      description: getDescription?.(item),
      icon: getIcon?.(item),
      chipColor: getChipColor?.(item),
      chipLabel: getChipLabel?.(item),
      controlType: isMulti ? 'checkbox' : 'radio',
      item,
      storedValue,
    };
  });
  const optionByKey = new Map(options.map((option) => [option.value, option]));
  // A cap only means anything in multi-select, so fold the two conditions into
  // one value rather than re-testing both wherever the cap is read.
  const cap = isMulti ? maxSelected : undefined;

  // Preserve the caller's selection order; drop values no longer available.
  const selectedOptions = value
    .map((storedValue) => optionByKey.get(keyOf(storedValue)))
    .filter(
      (option): option is InternalOption<TItem, TValue> => option != null,
    );

  const handleChange = (
    newValue: unknown,
    actionMeta: ActionMeta<InternalOption<TItem, TValue>>,
  ) => {
    // Selected options stay visible in the menu, so in single-select clicking
    // the current pick would otherwise clear the field. Radios don't untoggle,
    // so ignore it — the chip's remove button and the clear icon still do.
    if (!isMulti && actionMeta.action === 'deselect-option') {
      return;
    }
    const all = toArray<InternalOption<TItem, TValue>>(newValue);
    // react-select is always in multi mode (see below) and appends each pick,
    // so in single-select the last entry is the newest and replaces the rest.
    const chosen = isMulti ? all : all.slice(-1);
    onChange(
      chosen.map((option) => option.storedValue),
      chosen.map((option) => option.item),
    );
  };

  return (
    <Box xcss={wrapperStyles}>
      <Stack space="space.100" xcss={stackStyles}>
        <Stack space="space.050">
          {renderLabel && (
            <Box as="label" htmlFor={inputId}>
              {renderLabel({
                count: selectedOptions.length,
                total: items.length,
              })}
            </Box>
          )}
          <Box xcss={selectStyles}>
            <Select<InternalOption<TItem, TValue>, boolean>
              inputId={inputId}
              options={options}
              // Always multi, even for a single-select field: it is what makes
              // react-select render the selection as a removable chip rather
              // than bare text. Single selection is capped in handleChange.
              isMulti
              isDisabled={isDisabled}
              isSearchable={isSearchable}
              isClearable={isClearable}
              closeMenuOnSelect={!isMulti}
              // Selected options stay listed in both modes, so the leading
              // checkbox / radio reads as ticked for the current selection.
              hideSelectedOptions={false}
              placeholder={placeholder}
              value={selectedOptions}
              isOptionDisabled={
                isItemDisabled || cap != null
                  ? (option, selectValue) =>
                      Boolean(isItemDisabled?.(option.item)) ||
                      // Already-selected options stay enabled at the cap so
                      // they can still be removed.
                      (cap != null &&
                        selectValue.length >= cap &&
                        !selectValue.some(
                          (selected) => selected.value === option.value,
                        ))
                  : undefined
              }
              components={{
                Option: SelectOption as typeof components.Option,
                MultiValueLabel: ChipLabel as typeof components.MultiValueLabel,
              }}
              styles={
                chipStyles as StylesConfig<
                  InternalOption<TItem, TValue>,
                  boolean
                >
              }
              onChange={handleChange}
            />
          </Box>
        </Stack>
        {message != null && (
          <Inline space="space.050" alignBlock="center" xcss={messageStyles}>
            {messageTone === 'error' ? (
              <StatusErrorIcon
                label=""
                color={token('color.icon.danger')}
                spacing="none"
                testId="oauth-consent-field-message-error"
              />
            ) : (
              <StatusInformationIcon
                label=""
                color={token('color.icon.information')}
                spacing="none"
                testId="oauth-consent-field-message-information"
              />
            )}
            <Text size="small" color="color.text.subtlest">
              {message}
            </Text>
          </Inline>
        )}
      </Stack>
    </Box>
  );
}

export default MultiSelect;
